import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {toast} from 'sonner';
import {MousePointer2, Redo, X} from 'lucide-react';
import {Button} from '@tinct/web-components';
import type {AiChatInputPlugin} from '@tinct/web-ai';
import {AiChat, createInputPlugin} from '@tinct/web-ai';
import type {ContextContentBlock} from '@tinct/core-api-models';
import {useGetCampaignById} from '@tinct/core-api';
import {LandingPagePanel} from '../../../containers/landing-page-studio/components/landing-page-panel';
import {LandingPageCanvasPanel} from '../../../containers/landing-page-studio/components/canvas/landing-page-canvas-panel';
import {useStudioViewMode} from '../../../containers/landing-page-studio/hooks';
import {StudioLayout} from '../../../containers/landing-page-studio/components/studio-layout';
import {cacheBustHtmlKeys} from '../../../utils/cache-bust-html-keys';
import {useLandingPageChat} from './hooks/use-landing-page-chat';

// How long the fast campaign poll keeps running after an applied edit before
// falling back to the slow path (the version flip lands "shortly after" the
// data_update — the listener re-derives blocks asynchronously).
const VERSION_FLIP_TIMEOUT_MS = 30_000;

// Safety cap on the packaging fast-poll: `failed` is the deterministic stop
// signal, but a worker that dies without marking it would otherwise keep the
// poll hot forever.
const PACKAGING_POLL_TIMEOUT_MS = 30_000;

export type Step3EditContentProps = {
  campaignId: string;
  /** Wired to the empty state's redo action (regenerates the page). */
  onRegenerate?: () => void;
};

export const Step3EditContent = ({campaignId, onRegenerate}: Step3EditContentProps) => {
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  // Toolbar: hides the block overlays so the user can interact with the page
  // itself (links, hover states) instead of selecting blocks.
  const [hideBlocks, setHideBlocks] = useState(false);

  // Single viewport vs the multi-breakpoint canvas (persisted).
  const [viewMode, setViewMode] = useStudioViewMode();

  // The currentVersion.id an applied edit was streamed against; set on
  // `data_update` and drives the faster campaign poll below until the
  // listener flips the version (blocks re-derive asynchronously).
  const [awaitingVersionSince, setAwaitingVersionSince] = useState<string | null>(null);

  // Caps the packaging fast-poll below; declared before the query because its
  // refetchInterval callback closes over it.
  const [packagingPollExpired, setPackagingPollExpired] = useState(false);

  const {data: campaign} = useGetCampaignById(
    {
      id: campaignId,
      fields: {
        id: null,
        blocks: {
          limit: 200,
        },
        landingPage: {
          fields: {
            id: null,
            status: null,
            publishedUrl: null,
            currentVersion: {
              fields: {
                id: null,
                htmlKeys: null,
                packagingStatus: null,
                packagedAt: null,
              },
            },
          },
        },
      },
    },
    {
      enabled: !!campaignId,
      // Fast-poll while waiting for an edit's version flip AND while the
      // current version's artifact isn't current (`pending`/`packaging` —
      // covers both first packaging and custom-code re-packages, which flip
      // the status synchronously). `packaged` and `failed` both stop it.
      refetchInterval: (query) => {
        const version = query.state.data?.landingPage?.currentVersion;
        const packagingInFlight =
          !!version?.id &&
          version?.packagingStatus !== 'packaged' &&
          version?.packagingStatus !== 'failed';
        return awaitingVersionSince || (packagingInFlight && !packagingPollExpired)
          ? 2000
          : false;
      },
    }
  );

  const landingPageId = campaign?.landingPage?.id ?? null;
  const currentVersion = campaign?.landingPage?.currentVersion ?? null;
  const currentVersionId = currentVersion?.id ?? null;
  const packagingStatus = currentVersion?.packagingStatus ?? null;

  const isPackaging =
    !!currentVersionId &&
    packagingStatus !== 'packaged' &&
    packagingStatus !== 'failed';
  if (!isPackaging && packagingPollExpired) {
    setPackagingPollExpired(false);
  }
  useEffect(() => {
    if (!isPackaging) return;
    const timeout = setTimeout(
      () => setPackagingPollExpired(true),
      PACKAGING_POLL_TIMEOUT_MS,
    );
    return () => clearTimeout(timeout);
  }, [isPackaging, currentVersionId]);

  // Packaging failed: the previous artifact (if any) keeps serving; surface a
  // retry affordance — a new edit or a regeneration re-runs packaging.
  useEffect(() => {
    if (packagingStatus !== 'failed') return;
    toast.error(
      'The page preview could not be updated. Edit or regenerate the page to retry.',
    );
  }, [packagingStatus, currentVersionId]);

  // Cache-busted packaged preview URLs: re-packaging overwrites the same
  // URLs, so `packagedAt` — stamped when the artifact was written — is the
  // cache-busting key. It only moves when the artifact actually changed.
  const versionUrls = useMemo(
    () => cacheBustHtmlKeys(currentVersion?.htmlKeys, currentVersion?.packagedAt),
    [currentVersion?.htmlKeys, currentVersion?.packagedAt],
  );

  // Hold the previous version's URLs while the fresh artifact is packaging so
  // the canvas keeps showing the page instead of flashing a loading state.
  const [displayUrls, setDisplayUrls] = useState<Record<string, string> | null>(null);
  if (versionUrls && versionUrls !== displayUrls) {
    setDisplayUrls(versionUrls);
  }

  const blocks = useMemo(() => campaign?.blocks ?? [], [campaign?.blocks]);
  const selectedBlock = blocks.find((block) => block.id === selectedBlockId) ?? null;

  // A version flip re-derives the blocks with fresh ids — drop a selection
  // that no longer resolves. The backend would just ignore the unknown id
  // (global ask), but the chip must not linger on a dead block. State
  // derived during render, same pattern as the version-flip poll below.
  if (selectedBlockId && blocks.length > 0 && !selectedBlock) {
    setSelectedBlockId(null);
  }

  // ==========================================================================
  // Edit chat — the single edit path. Every applied edit lands as a new
  // immutable EDIT version announced by a `data_update` stream event.
  // ==========================================================================

  const {
    messages,
    eventBus,
    isLoadingMore,
    hasMore,
    handleSendMessage,
    handleLoadMore,
  } = useLandingPageChat({
    landingPageId,
    // The selection didn't resolve server-side (stale block) — the chip must
    // not keep promising a scope the agent isn't using.
    onContextIgnored: () => setSelectedBlockId(null),
  });

  // The raw message plus the selected block's id as structured context —
  // the backend resolves it to the data-lp path and composes the turn.
  const handleSendMessageWithBlock = useCallback(
    (message: string) => {
      handleSendMessage(message, selectedBlock?.id);
    },
    [selectedBlock?.id, handleSendMessage]
  );

  // The optimistic chip on the sent message: shown immediately from the local
  // selection, then replaced by the canonical context block the stream echoes
  // back (or stripped, if the selection was ignored and the turn ran globally).
  const composerContextBlock = useMemo<ContextContentBlock | null>(
    () =>
      selectedBlock
        ? {
            type: 'context',
            blockId: selectedBlock.id,
            blockVariantId: null,
            blockPath: null,
            label: selectedBlock.label ?? null,
          }
        : null,
    [selectedBlock],
  );

  // Applied edit → invalidate the campaign and fast-poll until the new
  // currentVersion (and then its packaged htmlKeys) plus the re-derived
  // blocks arrive.
  const queryClient = useQueryClient();
  const currentVersionIdRef = useRef(currentVersionId);
  useEffect(() => {
    currentVersionIdRef.current = currentVersionId;
  }, [currentVersionId]);

  useEffect(() => {
    const unsubscribe = eventBus.subscribe((event) => {
      if (event.type === 'content_block_start' && event.contentBlock.type === 'data_update') {
        void queryClient.invalidateQueries({queryKey: ['campaign', campaignId]});
        // Every new version re-derives the lead-form fields from the
        // rendered markup — drop the cached config so settings show them.
        void queryClient.invalidateQueries({queryKey: ['lead-form']});
        setAwaitingVersionSince(currentVersionIdRef.current);
      }
    });
    return () => unsubscribe();
  }, [eventBus, queryClient, campaignId]);

  // Stop the fast poll the moment the campaign serves the new version. State
  // derived during render (React-approved pattern, see use-campaign-chat) —
  // the condition self-extinguishes on the next render.
  if (awaitingVersionSince && currentVersionId && currentVersionId !== awaitingVersionSince) {
    setAwaitingVersionSince(null);
  }

  useEffect(() => {
    if (!awaitingVersionSince) return;
    const timeout = setTimeout(() => setAwaitingVersionSince(null), VERSION_FLIP_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [awaitingVersionSince]);

  const handleHideBlocksChange = useCallback((hidden: boolean) => {
    setHideBlocks(hidden);
    if (hidden) setSelectedBlockId(null);
  }, []);

  // ==========================================================================
  // Composer input plugin: the selected block as a removable chip, its
  // enable/disable switch, and the per-block quick actions — all sent through
  // the chat like any other message.
  // ==========================================================================

  const inputPlugins = useMemo<AiChatInputPlugin[]>(() => {
    if (!selectedBlock) return [];
    return [
      createInputPlugin({
        id: 'selected-block',
        // Below the suggestions input plugin (10) so an agent question replaces the composer.
        priority: 5,
        shouldActivate: () => true,
        render: () => (
          <div className="flex flex-col gap-2 px-4 pt-4">
            <div className="flex items-center gap-2">
              <span className="flex h-8 shrink-0 items-center gap-1 rounded-full border border-border bg-muted p-2">
                <MousePointer2 className="size-3" />
                <span className="max-w-40 truncate px-1 text-sm text-foreground">
                  {selectedBlock.label || 'Selected block'}
                </span>
                <button
                  type="button"
                  aria-label="Deselect block"
                  onClick={() => setSelectedBlockId(null)}
                  className="flex size-4 items-center justify-center rounded-full bg-input text-foreground"
                >
                  <X className="size-3" />
                </button>
              </span>
            </div>
          </div>
        ),
      }),
    ];
  }, [selectedBlock]);

  // Failed with a previous artifact: keep showing it (toast carries the
  // notice). Failed with nothing packaged yet: full error state.
  const packagingFailedState =
    packagingStatus === 'failed' && !displayUrls ? (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
        <p>The page preview could not be generated.</p>
        {onRegenerate && (
          <Button variant="outline" onClick={onRegenerate}>
            <Redo className="size-4" />
            Regenerate page
          </Button>
        )}
      </div>
    ) : undefined;

  return (
    <StudioLayout
      chatPanel={
        <AiChat
          theme="compact"
          eventBus={eventBus}
          messages={messages}
          onSendMessage={handleSendMessageWithBlock}
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          inputPlugins={inputPlugins}
          composerContextBlock={composerContextBlock}
          placeholder={
            selectedBlock ? 'Ask AI to edit this block…' : 'Ask AI to edit your page…'
          }
          emptyState={
            <div className="flex flex-col gap-4 pt-4 text-base text-foreground">
              <p>Here&apos;s your first draft.</p>
              <p>
                Take a look on the right — check the copy, the colors, and
                whether it feels on-brand and lines up with your campaign.
              </p>
              <p>
                Tell me what to change, block by block, or hit Publish when
                it&apos;s ready.
              </p>
              {onRegenerate && (
                <div className="flex items-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    title="Regenerate page"
                    onClick={onRegenerate}
                  >
                    <Redo className="size-4" />
                  </Button>
                </div>
              )}
            </div>
          }
        />
      }
      hideBlocks={hideBlocks}
      onHideBlocksChange={handleHideBlocksChange}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
    >
      {viewMode === 'canvas' ? (
        <LandingPageCanvasPanel
          key={`${campaignId}-edit-canvas`}
          landingPageUrls={displayUrls}
          loading={!displayUrls && packagingStatus !== 'failed'}
          blocks={hideBlocks ? [] : blocks}
          includeDisabledBlocks
          selectedBlockId={selectedBlockId}
          onBlockSelect={setSelectedBlockId}
          errorState={packagingFailedState}
        />
      ) : (
        <LandingPagePanel
          key={`${campaignId}-edit`}
          landingPageUrls={displayUrls}
          loading={!displayUrls && packagingStatus !== 'failed'}
          blocks={hideBlocks ? [] : blocks}
          includeDisabledBlocks
          selectedBlockId={selectedBlockId}
          onBlockSelect={setSelectedBlockId}
          errorState={packagingFailedState}
        />
      )}
    </StudioLayout>
  );
};
